import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Slider } from '@flipster/react/design-system-legacy/slider';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Explain } from '@flipster/react/design-utils/explain';
import Formatted from '@flipster/react/design-utils/formatted';
import PlusIcon from '@flipster/react/icon/generated/PlusIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { numberFormatter } from '@flipster/utils/num';

import { NumberInputWithSuffix } from '../../../../../input/number-input-with-suffix';
import { useDepositModal } from '../../../../../shared/wallet-modal/deposit';
import { copyTradingSettingsBunja } from '../../state';

interface CopyAmountSectionProps {
  onClose: () => void;
}

export const CopyAmountSection = ({ onClose }: CopyAmountSectionProps) => {
  const openDepositModal = useDepositModal();
  const {
    portfolioAtom,
    availableFundsAtom,
    setCopyAmountText,
    setCopyAmountPct,
    copyAmountTextAtom,
    copyAmountPctAtom,
    copyAmountErrorMessageAtom,
  } = useBunja(copyTradingSettingsBunja);
  const portfolio = useAtomValue(portfolioAtom);
  const availableFunds = useAtomValue(availableFundsAtom);
  const copyAmountText = useAtomValue(copyAmountTextAtom);
  const copyAmountPct = useAtomValue(copyAmountPctAtom);
  const copyAmountErrorMessage = useAtomValue(copyAmountErrorMessageAtom);
  const hasError = Boolean(copyAmountErrorMessage);
  return (
    <div className={stack({ gap: 4 })}>
      <div className={stack({ gap: 8 })}>
        <div className={flex({ justify: 'space-between', gap: 8 })}>
          <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
            <FormattedMessage id='Copy.r251107.Copy_amount' />
          </p>
          <div className={hstack({ gap: 4 })}>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
              <FormattedMessage id='Common.r251107.Available' />
            </p>
            <div className={hstack({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
              <Formatted.Number value={availableFunds} suffix={UsdtCurrency} precision={UsdtPrecision} toFixed />
              <IconButton
                hierarchy='quaternary'
                size='small'
                icon={<PlusIcon />}
                onClick={() => {
                  onClose();
                  openDepositModal();
                }}
              />
            </div>
          </div>
        </div>
        <div
          className={hstack({
            justify: 'space-between',
            gap: 16,
            p: 16,
            borderColor: hasError ? 'border.state.negative' : 'border.default.level-2',
            borderWidth: '1px',
            borderStyle: 'solid',
            rounded: 8,
            textStyle: 'Text/M/Regular',
            transition: 'border-color 200ms',
            _focusWithin: {
              borderColor: hasError ? 'border.state.negative' : 'border.default.level-4',
            },
          })}
        >
          <NumberInputWithSuffix
            value={copyAmountText}
            onChange={setCopyAmountText}
            precision={UsdtPrecision}
            placeholder={`${numberFormatter(portfolio?.minInvestAmountUsdt)} ~ ${numberFormatter(portfolio?.maxInvestAmountUsdt)}`}
            suffix={
              <div className={hstack({ gap: 8 })}>
                <Formatted.Percent value={copyAmountPct} css={{ color: 'content.default.level-1' }} />
                <p className={css({ color: 'content.default.level-5' })}>{UsdtCurrency}</p>
              </div>
            }
            className={css({ justifyContent: 'space-between' })}
          />
        </div>
        <Explain.Error>{copyAmountErrorMessage}</Explain.Error>
      </div>
      <Slider
        css={{ my: 12, '& button': { color: 'content.default.level-2!' } }}
        value={copyAmountPct.toNumber()}
        onChange={setCopyAmountPct}
        min={0}
        max={100}
        step={1}
        marks={[0, 25, 50, 75, 100]}
        getLabel={(mark) => `${mark}%`}
      />
    </div>
  );
};
